import React from "react";
import { Box, Button, Tooltip } from "@mui/material";
import tokens from "../../theme/tokens";
import { INTERVIEWS_ENABLED } from "../../config/features";
import { shortlistReason, sharedDecision } from "./decisionGuard";
import { pauseMode, pauseReason } from "./pauseGuard";
import { DECISION } from "./candidateRow";

const { UNDECIDED, SHORTLISTED, BACKLOG, REJECTED } = DECISION;

const { color, font, radius } = tokens;

const kbdSx = { ml: 0.75, font: `400 10px ${font.mono}`, color: color.textMuted };

/**
 * A disabled MUI Button swallows pointer events, so a Tooltip on it never fires.
 * Wrapping in a span restores the hover target — this is what turns the old red
 * error banners into on-demand tooltips.
 */
const GuardedButton = ({ reason, children, ...props }) => {
  const button = (
    <span style={{ display: "inline-flex" }}>
      <Button {...props} disabled={Boolean(reason) || props.disabled}>
        {children}
      </Button>
    </span>
  );
  return reason ? (
    <Tooltip title={reason} arrow>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

const outlineSx = {
  border: `1px solid ${color.borderInput}`,
  borderRadius: `${radius.button}px`,
  font: `500 12px ${font.sans}`,
  color: color.text,
  px: 1.5,
  py: 0.75,
};

const SelectionBar = ({
  selectedCount,
  stage,
  guards,
  picked,
  batchActive,
  screeningLoading,
  pauseLoading,
  decisionLoading,
  onClear,
  onDecide,
  onPauseResume,
  onScreening,
  onInterview,
  onStartCalls,
}) => {
  if (!selectedCount) return null;

  const { missingPhone, alreadyScreened, alreadyInterviewed, notScreened, screeningActive } =
    guards;

  const names = (list) => list.map((c) => c.name || c.ResumeId).join(", ");

  // Screening and interview are independent — each blocks only its own re-scheduling.
  const screeningReason = missingPhone.length
    ? `Screening can't be scheduled — ${names(missingPhone)} ${
        missingPhone.length === 1 ? "has" : "have"
      } no phone number.`
    : alreadyScreened.length
    ? `Already screened — ${names(alreadyScreened)}.`
    : "";

  const interviewReason = alreadyInterviewed.length
    ? `Already selected for interview — ${names(alreadyInterviewed)}.`
    : "";

  const callsReason = batchActive ? "A batch of AI calls is already running." : "";

  // Shortlist is the only guarded decision: "yes" demands evidence. Reject and backlog are always
  // available — you need proof to advance someone and none to pass on them.
  const shortlistReasonText = shortlistReason({ stage, notScreened, screeningActive });

  // Undo appears only once every selected candidate shares one decision — there is nothing
  // coherent to undo across a mixed selection, and guessing would un-reject someone by accident.
  const undone = sharedDecision(picked);

  // Pause is the mirror of shortlist — it acts on the calls shortlist can't touch. The button
  // flips to Resume only when the whole selection is paused, so one click never means both.
  const mode = pauseMode(guards);
  const pauseReasonText = pauseReason({ stage, ...guards });

  return (
    <Box
      sx={{
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        bgcolor: color.surface,
        borderTop: `1px solid ${color.border}`,
        px: 2.25,
        py: 1.25,
      }}
    >
      <Box sx={{ font: `600 12px ${font.sans}`, color: color.text }}>
        {selectedCount} selected
      </Box>
      <Box
        onClick={onClear}
        sx={{ font: `400 11px ${font.mono}`, color: color.textMuted, cursor: "pointer" }}
      >
        esc to clear
      </Box>

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
        <GuardedButton
          sx={outlineSx}
          reason={pauseReasonText}
          disabled={pauseLoading}
          onClick={() => onPauseResume(mode !== "resume")}
        >
          {mode === "resume" ? "Resume screening" : "Pause screening"}
        </GuardedButton>

        {undone ? (
          <Button sx={outlineSx} disabled={decisionLoading} onClick={() => onDecide(UNDECIDED)}>
            Undo decision
          </Button>
        ) : null}

        <GuardedButton
          sx={outlineSx}
          disabled={decisionLoading}
          onClick={() => onDecide(BACKLOG)}
        >
          Backlog
        </GuardedButton>

        <GuardedButton
          sx={outlineSx}
          disabled={decisionLoading}
          onClick={() => onDecide(REJECTED)}
        >
          Reject
        </GuardedButton>

        <GuardedButton
          sx={outlineSx}
          reason={shortlistReasonText}
          disabled={decisionLoading}
          onClick={() => onDecide(SHORTLISTED)}
        >
          Shortlist
        </GuardedButton>

        <GuardedButton
          variant="contained"
          // sx={outlineSx}
          reason={screeningReason}
          disabled={screeningLoading}
          onClick={onScreening}
        >
          Screening call
          <Box component="span" sx={kbdSx}>
            S
          </Box>
        </GuardedButton>

        {/* Hidden while screening-only. "Start AI calls" especially: a stray click there
            places a real phone call to a real candidate, which cannot be undone. */}
        {INTERVIEWS_ENABLED && (
          <>
            <GuardedButton sx={outlineSx} reason={interviewReason} onClick={onInterview}>
              Interview
              <Box component="span" sx={kbdSx}>
                I
              </Box>
            </GuardedButton>

            <GuardedButton variant="contained" reason={callsReason} onClick={onStartCalls}>
              Start AI calls
            </GuardedButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SelectionBar;
